import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, Container, Paper } from "@mui/material";
import { motion } from "framer-motion";
import SEO from '../components/SEO';
import LazyImage from "../components/LazyImage"; // Add this import

export default function VersusLobby() {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <SEO 
        title="Join Live Anime Battles - Versus Lobby"
        description="Join or create live anime character battle rooms. Wait for other fans to join and vote together on epic powerscaling matchups in real-time!"
        keywords="anime battle lobby, live voting rooms, real-time anime battles, powerscaling community, anime character matchups"
        url="/versus-lobby"
      />
      <Box sx={{ minHeight: "100vh" }}>
        {/* Spacer for navbar */}
        <Box sx={{ height: { xs: 72, sm: 80 } }} />

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Title Section */}
            <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h2"
                  sx={{
                    mb: 8,
                    mt: 10, // Add more top margin
                    textAlign: "center",
                    fontWeight: 900,
                    letterSpacing: 2,
                    fontSize: { xs: 36, sm: 48, md: 56 },
                    background:
                      "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  CHOOSE YOUR BATTLE
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#e0e0e0",
                    fontWeight: 600,
                    mb: 4,
                    fontSize: { xs: 16, sm: 20 },
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.4,
                  }}
                >
                  Select your preferred battle mode and start voting
                </Typography>
              </motion.div>
            </Box>

            {/* Battle Mode Cards */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 6,
                justifyContent: "center",
                alignItems: "stretch",
                maxWidth: 1200,
                mx: "auto",
                mb: 6,
              }}
            >
              {/* Versus Live Card */}
              <motion.div variants={itemVariants} style={{ flex: 1 }}>
                <Paper
                  sx={{
                    background: "rgba(35, 35, 35, 0.85)",
                    border: "2px solid #3e3e3e",
                    borderRadius: 4,
                    p: 5,
                    textAlign: "center",
                    minHeight: 420,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#f49330",
                      boxShadow: "0 8px 32px rgba(244, 147, 48, 0.2)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box>
                    {/* Icon */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                      <LazyImage
                        src="https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/icons/kunai.png"
                        alt="Versus Live"
                        threshold={0.2}
                        sx={{
                          width: 64,
                          height: 64,
                          filter: "brightness(0) saturate(100%) invert(100%)"
                        }}
                      />
                    </Box>

                    <Typography
                      variant="h3"
                      sx={{
                        color: "#fff",
                        fontWeight: 800,
                        mb: 3,
                        fontSize: { xs: 24, sm: 30 },
                      }}
                    >
                      VERSUS LIVE
                    </Typography>

                    <Typography
                      sx={{
                        color: "#bbb",
                        fontSize: { xs: 14, sm: 16 },
                        lineHeight: 1.5,
                        mb: 4,
                      }}
                    >
                      Vote alongside
                      other manga fans and see results update live. Your votes help
                      determine the ultimate character rankings.
                    </Typography>

                    {/* Features */}
                    <Box sx={{ mb: 4, textAlign: "left" }}>
                      <Typography
                        sx={{
                          color: "#f49330",
                          fontWeight: 600,
                          mb: 2,
                          fontSize: 16,
                        }}
                      >
                        Features:
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Typography
                          sx={{
                            color: "#e0e0e0",
                            mb: 1,
                            fontSize: 14,
                          }}
                        >
                          • Real-time voting with other users
                        </Typography>
                        <Typography
                          sx={{
                            color: "#e0e0e0",
                            mb: 1,
                            fontSize: 14,
                          }}
                        >
                          • Live results and rankings
                        </Typography>
                        <Typography
                          sx={{
                            color: "#e0e0e0",
                            fontSize: 14,
                          }}
                        >
                          • Community-driven battles
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/versus-live")}
                    sx={{
                      background:
                        "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                      color: "#181818",
                      fontWeight: 700,
                      fontSize: { xs: 16, sm: 20 },
                      px: 5,
                      py: 2.5,
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
                      "&:hover": {
                        background:
                          "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                        boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    JOIN LIVE BATTLE
                  </Button>
                </Paper>
              </motion.div>

              {/* Solo Versus Card */}
              <motion.div variants={itemVariants} style={{ flex: 1 }}>
                <Paper
                  sx={{
                    background: "rgba(35, 35, 35, 0.85)",
                    border: "2px solid #3e3e3e",
                    borderRadius: 4,
                    p: 5,
                    textAlign: "center",
                    minHeight: 420,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#6366f1",
                      boxShadow: "0 8px 32px rgba(99, 102, 241, 0.2)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box>
                    {/* Icon */}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                      <LazyImage
                        src="https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/images/icons/katana.png"
                        alt="Solo Versus"
                        threshold={0.2}
                        sx={{
                          width: 64,
                          height: 64,
                          filter: "brightness(0) saturate(100%) invert(100%)"
                        }}
                      />
                    </Box>

                    <Typography
                      variant="h3"
                      sx={{
                        color: "#fff",
                        fontWeight: 800,
                        mb: 3,
                        fontSize: { xs: 24, sm: 30 },
                      }}
                    >
                      SOLO VERSUS
                    </Typography>

                    <Typography
                      sx={{
                        color: "#bbb",
                        fontSize: { xs: 14, sm: 16 },
                        lineHeight: 1.5,
                        mb: 4,
                      }}
                    >
                      Explore different character
                      matchups. Choose specific tags and categories to customize your
                      battles and discover new favorites.
                    </Typography>

                    {/* Features */}
                    <Box sx={{ mb: 4, textAlign: "left" }}>
                      <Typography
                        sx={{
                          color: "#6366f1",
                          fontWeight: 600,
                          mb: 2,
                          fontSize: 16,
                        }}
                      >
                        Features:
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Typography
                          sx={{
                            color: "#e0e0e0",
                            mb: 1,
                            fontSize: 14,
                          }}
                        >
                          • Personal voting experience
                        </Typography>
                        <Typography
                          sx={{
                            color: "#e0e0e0",
                            mb: 1,
                            fontSize: 14,
                          }}
                        >
                          • Customizable tag filters
                        </Typography>
                        <Typography
                          sx={{
                            color: "#e0e0e0",
                            fontSize: 14,
                          }}
                        >
                          • Explore specific categories
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/character-voting")}
                    sx={{
                      background: "linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: { xs: 16, sm: 20 },
                      px: 5,
                      py: 2.5,
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(99, 102, 241, 0.4)",
                      "&:hover": {
                        background: "linear-gradient(45deg, #4f46e5 30%, #7c3aed 90%)",
                        boxShadow: "0 6px 25px rgba(99, 102, 241, 0.6)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    START SOLO MODE
                  </Button>
                </Paper>
              </motion.div>
            </Box>

            {/* Additional Info Section */}
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  background: "rgba(35, 35, 35, 0.6)", // Made slightly more transparent
                  borderRadius: 4,
                  border: "1px solid rgba(244, 147, 48, 0.3)",
                  p: 4,
                  textAlign: "center",
                  maxWidth: 700,
                  mx: "auto",
                  backdropFilter: "blur(8px)", // Add glass effect
                  WebkitBackdropFilter: "blur(8px)",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: "#f49330",
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: 20, sm: 24 },
                  }}
                >
                  Ready to Vote?
                </Typography>
                <Typography
                  sx={{
                    color: "#e0e0e0",
                    fontSize: { xs: 14, sm: 16 },
                    lineHeight: 1.5,
                  }}
                >
                  Choose your battle mode and help determine which manga characters
                  reign supreme. Whether you join the live community or practice
                  solo, every vote counts towards building the ultimate character
                  rankings!
                </Typography>
              </Box>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </>
  );
}